import { useMemo } from 'react';
import type { KmTreeNodeData } from './kmTreeUtils';

/** Наборы, по которым ведётся перетаскивание в дереве структуры */
export type KmTreeIndexes = {
  /** Группы без вложенных элементов — только они принимают вложение внутрь по середине строки */
  leafGroupIds: Set<string>;
  /** Свёрнутые группы с вложенными элементами — цели авто-разворачивания при задержке наведения */
  collapsedGroupIds: Set<string>;
  /** Узел → множество его потомков: чтобы не разворачивать и не занимать собственное поддерево */
  descendantsByNodeId: Map<string, Set<string>>;
  /** Узел → подпись: по ней рисуется плавающая копия перетаскиваемого узла */
  labelByNodeId: Map<string, string>;
};

/**
 * Разбор дерева структуры на наборы для перетаскивания. Считается одним проходом по дереву
 * и живёт отдельно от компонента: сами наборы к разметке отношения не имеют.
 */
export const useKmTreeIndexes = (nodes: KmTreeNodeData[], expandedIds: string[]): KmTreeIndexes => {
  const expandedIdSet = useMemo(() => new Set(expandedIds), [expandedIds]);

  return useMemo(() => {
    const leafGroupIds = new Set<string>();
    const collapsedGroupIds = new Set<string>();
    const descendantsByNodeId = new Map<string, Set<string>>();
    const labelByNodeId = new Map<string, string>();

    const walk = (items: KmTreeNodeData[]): Set<string> => {
      const subtree = new Set<string>();
      for (const item of items) {
        labelByNodeId.set(item.id, item.label);
        const hasChildren = Boolean(item.children?.length);
        if (item.type === 'group') {
          if (!hasChildren) leafGroupIds.add(item.id);
          else if (!expandedIdSet.has(item.id)) collapsedGroupIds.add(item.id);
        }

        const childIds = hasChildren ? walk(item.children!) : new Set<string>();
        descendantsByNodeId.set(item.id, childIds);
        subtree.add(item.id);
        for (const id of childIds) subtree.add(id);
      }
      return subtree;
    };
    walk(nodes);

    return { leafGroupIds, collapsedGroupIds, descendantsByNodeId, labelByNodeId };
  }, [nodes, expandedIdSet]);
};
